import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import { setField, setIsClass } from '../../../redux/formData';
import { Card, CardContent } from '@mui/material';
import MuxUploader from "@mux/mux-uploader-react";
import axiosConfig from '../../../data/utils/axiosConfigInstance';
import { fileUploadApi } from '../../../data/api/fileUpload';
import Spinner from '../../../component/Spinner';
import { MUX_UPLOAD_STATUS } from '@/constants/muxUploadStatus';
import localStorageKeys from '@/constants/localStorageKeys'
import { setDataInBrowser, getDataFromBrowser, removeDataFromBrowser } from '@/utils/browserStorage';



const Upload = ({ formData, dispatch }) => {
  const [selectedValue, setSelectedValue] = useState("library")
  const [uploadState, setUploadState] = useState({
    video: { url: '', id: '', loading: false, polling: false },
    trailer: { url: '', id: '', loading: false, polling: false },
    thumbnail: { loading: false },
    trailerThumbnail: { loading: false },
  });
  const [preview, setPreview] = useState(null);
  const [trailerPreview, setTrailerPreview] = useState(null)

  const savePollingState = ({ videoId = null, trailerId = null }) => {
    const state = {
      video: videoId ?? null,
      trailer: trailerId ?? null,
      timestamp: Date.now()
    };
    setDataInBrowser(localStorageKeys.MUX_POLLING_STATE, state);
  };

  const loadPollingState = () => {
    const state = getDataFromBrowser(localStorageKeys.MUX_POLLING_STATE);
      if (!state) return null;
      
      // Ignore if older than 2 hours
      const TWO_HOURS_MS = 2 * 60 * 60 * 1000
      if (Date.now() - state.timestamp > TWO_HOURS_MS) {
        removeDataFromBrowser(localStorageKeys.MUX_POLLING_STATE);
        return null;
      }
      return state;
  };

  const clearPollingState = () => {
    removeDataFromBrowser(localStorageKeys.MUX_POLLING_STATE);
  };

  const handleTypeSelect = (e) => {
    const isClass = e.target.value === "class";
    dispatch(setIsClass(isClass));
    setSelectedValue(e.target.value);
  };
  const handleImageChange = async (e, type = 'main') => {
    const file = e.target.files[0];
    if (!file) return;

    const key = type === 'trailer' ? 'trailerThumbnail' : 'thumbnail';
    setUploadState(prev => ({ ...prev, [key]: { loading: true } }));

    try {
      const uploadResponse = await fileUploadApi(file);
      const cdnLink = uploadResponse?.data?.cdnLink;

      if (type === 'trailer') {
        dispatch(setField("trailerThumnailLink", cdnLink));
        setTrailerPreview(URL.createObjectURL(file));
      } else {
        dispatch(setField("thumnailLink", cdnLink));
        setPreview(URL.createObjectURL(file));
      }
    } catch (err) {
      console.error("Image Upload Failed:", err);
    } finally {
      setUploadState(prev => ({ ...prev, [key]: { loading: false } }));
    }
  };


  const getDirectUploadUrl = async (type = "video") => {
    if (!formData.title.trim()) return;

    clearPollingState();

    setUploadState(prev => ({
      ...prev,
      [type]: { ...prev[type], loading: true }
    }));

    try {
      const title = type === "trailer" ? `${formData.title.trim()} - trailer` : formData.title.trim();
      const res = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/admin/direct-upload`, { title });
      const { url, uploadId } = res.data.data;

      setUploadState(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          url,
          id: uploadId,
          loading: false
        }
      }));
    } catch (err) {
      console.error(err);
      setUploadState(prev => ({
        ...prev,
        [type]: { ...prev[type], loading: false }
      }));
    }
  };

  function formatDuration(seconds) {
    return Math.floor(Number(seconds));

  }


  const pollMuxStatus = ({ uploadId, onReady, stopPolling }) => {
    const interval = setInterval(async () => {
      try {
        const res = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/admin/direct-upload/${uploadId}`);
        const status = res?.data?.data?.status;
        if (status === MUX_UPLOAD_STATUS.READY) {
          clearInterval(interval);
          stopPolling();
          const muxLink = res?.data?.data?.muxVideoLink;
          const duration = res?.data?.data?.duration
          const playbackId = res?.data?.data?.playback_id
          dispatch(setField("playbackId", playbackId))
          if (muxLink) onReady(muxLink, duration);
        }
      } catch (error) {
        console.error(`Error polling Mux status for uploadId ${uploadId}:`, error);
      }
    }, 15000);

    return interval;
  };

  useEffect(() => {
    let mainInterval, trailerInterval;

    // Load saved polling state on mount
    const savedState = loadPollingState();
    if (savedState) {
      if (savedState.video && !uploadState.video.id) {
        setUploadState(prev => ({
          ...prev,
          video: { ...prev.video, id: savedState.video, polling: true }
        }));
      }
      if (savedState.trailer && !uploadState.trailer.id) {
        setUploadState(prev => ({
          ...prev,
          trailer: { ...prev.trailer, id: savedState.trailer, polling: true }
        }));
      }
    }

    if (uploadState.video.id && uploadState.video.polling) {
      savePollingState(uploadState.video.id, uploadState.trailer.id);
      
      mainInterval = pollMuxStatus({
        uploadId: uploadState.video.id,
        onReady: (link, duration) => {
          const formattedDuration = formatDuration(duration);
          dispatch(setField("muxVideoLink", link));
          dispatch(setField("duration", formattedDuration));
          
          if (!uploadState.trailer.polling) {
            clearPollingState();
          }
        },
        stopPolling: () => setUploadState((prev) => ({
          ...prev,
          video: { ...prev.video, polling: false }
        })),
      });
    }

    if (uploadState.trailer.id && uploadState.trailer.polling) {
      savePollingState(uploadState.video.id, uploadState.trailer.id);
      
      trailerInterval = pollMuxStatus({
        uploadId: uploadState.trailer.id,
        onReady: (link, duration) => {
          const formattedDuration = formatDuration(duration);
          dispatch(setField("muxTrailerLink", link));
          dispatch(setField("trailerDuration", formattedDuration));
          
          if (!uploadState.video.polling) {
            clearPollingState();
          }
        },
        stopPolling: () => setUploadState((prev) => ({
          ...prev,
          trailer: { ...prev.trailer, polling: false }
        })),
      });
    }

    return () => {
      if (!uploadState.video.polling && !uploadState.trailer.polling) {
        clearInterval(mainInterval);
        clearInterval(trailerInterval);
        clearPollingState();
      }
    };
  }, [uploadState.video.id, uploadState.video.polling, uploadState.trailer.id, uploadState.trailer.polling]);

  const hasActivePolling = () => {
    const saved = loadPollingState();
    return saved && (saved.video || saved.trailer);
  };

  useEffect(() => {
    if (uploadState.video.id) {
      dispatch(setField("muxUploadId", uploadState.video.id));
      dispatch(setField("muxStatus", MUX_UPLOAD_STATUS.PREPARING));
    }
  }, [uploadState.video.id]);

  useEffect(() => {
    if (uploadState.trailer.id) {
      dispatch(setField("muxTrailerUploadId", uploadState.trailer.id));
    }
  }, [uploadState.trailer.id]);

  return (
    <>
        <CardContent>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "2rem" }}>
            <TextField
              fullWidth
              name="title"
              value={formData.title}
              variant="outlined"
              placeholder="Enter video title"
              onChange={(e) => dispatch(setField(e.target.name, e.target.value))}
            />


            <div style={{ display: "flex", gap: "2rem", alignItems: "center", marginBottom: "2rem" }}>
              <span>Upload Video Here</span>
              {uploadState.video.url === '' ? (
                <Button
                  component="label"
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                  onClick={() => getDirectUploadUrl("video")}
                  disabled={!formData.title.trim()}
                >


                  {uploadState.video.loading === true ? "Loading" : "Upload Video"}
                </Button>) : (
                <MuxUploader endpoint={uploadState.video.url}
                  onSuccess={(event) => {
                    setUploadState((prev) => ({
                      ...prev,
                      video: { ...prev.video, polling: true }
                    }))
                  }}
                  onUploadError={(event) => {
                    console.error('Mux upload error', event.detail);
                  }} />
              )}
            </div>
            <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
              <span>Upload Video Thumbnail</span>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                disabled={!formData.title.trim()}
              >
                {uploadState.thumbnail.loading ? "Uploading..." : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleImageChange(e, 'main')}
                />
              </Button>

              {preview && (
                <div className="mt-2">
                  <img src={preview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
            <span style={{ fontSize: "1rem", fontWeight: 600 }}>Select the type of video</span>

            <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Radio
                checked={selectedValue === "library"}
                onChange={handleTypeSelect}
                value="library"
                name="radio-buttons"
                inputProps={{ "aria-label": "Library" }}
              />
              Library
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Radio
                checked={selectedValue === "class"}
                onChange={handleTypeSelect}
                value="class"
                name="radio-buttons"
                inputProps={{ "aria-label": "Class" }}
              />
              Class
            </label>
          </div>

          {/* Conditional: Trailer Uploads */}
          {selectedValue === "class" && (
            <>
              {/* Trailer Video Upload */}
              <div style={{ display: "flex", gap: "2rem", alignItems: "center", marginBottom: "2rem" }}>
                <span>Upload Trailer Video</span>
                {uploadState.trailer.url === '' ? (
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    onClick={() => getDirectUploadUrl("trailer")}
                    disabled={!formData.title.trim()}
                  >
                    {uploadState.trailer.loading === true ? "Loading" : "Upload Trailer"}
                  </Button>) : (
                  <MuxUploader endpoint={uploadState.trailer.url}
                    onSuccess={(event) => {
                      setUploadState((prev) => ({
                        ...prev,
                        trailer: { ...prev.trailer, polling: true }
                      }))
                    }}
                    onUploadError={(event) => {
                      console.error('Mux upload error', event.detail);
                    }} />
                )}
              </div>

              {/* Trailer Thumbnail Upload */}
              <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
                <span>Upload Trailer Thumbnail</span>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={!formData.title.trim()}
                >
                  {uploadState.trailerThumbnail.loading ? "Uploading..." : "Upload Trailer Thumbnail"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImageChange(e, 'trailer')}
                  />
                </Button>
                {trailerPreview && (
                  <div className="mt-2">
                    <img src={trailerPreview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                  </div>
                )}

              </div>
            </>
          )}

        </CardContent>
    </>
  );
};

export default Upload;
